import type { Theme } from '@emotion/react';
import { useTheme, css } from '@emotion/react';
import { Box, Draft, Hr, Inline, Link, ResiLogoBanner, Stack, Text } from '@resi-media/resi-ui';
import { FOOTER_PADDING } from '@sites/constants/layout';
import { useFeed } from '@sites/contexts/feedContext';
import { usePrefix } from '@studio/hooks/use-prefix';
import { PushpayIcon } from '../PushpayIcon';

const S = {
  container: (theme: Theme, hasPlaylists: boolean) =>
    css({
      padding: `${theme.spacing.s} ${theme.spacing.m}`,
      color: theme.palette.common.white,

      [theme.mq.lg]: {
        padding: `${theme.spacing.m} ${FOOTER_PADDING}px`,
      },

      ...(hasPlaylists && { marginTop: `${theme.typography.pxToRem(80)}` }),
    }),
  logoLink: css({
    transition: 'transform 100ms ease',

    '&:hover': {
      transform: 'scale(1.05)',
    },
  }),
};

const Footer = () => {
  const { commonT, linkT, prefixNS } = usePrefix('components:', 'footer');
  const theme = useTheme();
  const utils = Draft.useUtils<Date>();
  const { playlists } = useFeed();

  return (
    <footer css={(theme) => S.container(theme, Boolean(playlists?.length))} data-testid="app-footer">
      <Stack gap={10}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Inline alignItems="center" color="contrast" gap={{ xs: 'm', xl: 48 }}>
            <Link
              aria-label={prefixNS('resiAriaLabel')}
              colorVariant="inherit"
              css={S.logoLink}
              data-testid="resi-logo-link"
              href={linkT('resi')}
            >
              <ResiLogoBanner color={theme.palette.contrast} fontSize={theme.typography.pxToRem(61)} />
            </Link>
            <Link
              aria-label={prefixNS('pushpayAriaLabel')}
              colorVariant="inherit"
              css={S.logoLink}
              data-testid="pushpay-logo-link"
              href={linkT('pushpay')}
            >
              <PushpayIcon color={theme.palette.contrast} fontSize={theme.typography.pxToRem(85)} />
            </Link>
          </Inline>
        </Box>
        <Hr />
        <Text>
          <span>{`© ${utils.format(new Date(), 'year')} Resi Media LLC`}</span> and Pushpay<sup>&reg;</sup> Ltd.{' '}
          {commonT('allRightsReserved')}
        </Text>
      </Stack>
    </footer>
  );
};

export { Footer };
